<script lang="ts">
  import { onMount } from 'svelte'
  import { navigate } from '$lib/router'
  import { currentTheme, cycleTheme, themeMeta } from '$lib/theme'
  import { cityList, ensureLoaded, deleteCity, renamePlan, refresh } from './stores'
  import CityCard from './CityCard.svelte'
  import EmptyState from './EmptyState.svelte'
  import { downloadImportJson } from '../city-view/export-json'

  const activeTheme = $derived(themeMeta($currentTheme))

  interface Props {
    onImport: () => void
    onFileDrop: (file: File) => void
  }

  const { onImport, onFileDrop }: Props = $props()

  onMount(() => {
    ensureLoaded()
    refresh()
  })

  async function handleDelete(importedCityId: number, name: string) {
    if (!confirm(`Delete "${name}"? This permanently removes the city and any saved snapshots.`)) return
    await deleteCity(importedCityId)
  }

  async function handleRename(planId: number, currentName: string) {
    const next = prompt('Rename city', currentName)
    if (!next || next.trim() === '' || next === currentName) return
    await renamePlan(planId, next.trim())
  }
</script>

<main class="min-h-screen bg-bg-base px-8 py-8 text-text-primary">
  <header class="mx-auto flex max-w-6xl items-center justify-between">
    <div>
      <h1 class="brand-mark text-2xl tracking-tight">
        <span class="ember-letter">N</span>exus <span class="ember-letter">F</span>orge
      </h1>
      <p class="text-sm text-text-secondary">Plan your city before you commit in-game.</p>
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="rounded px-3 py-2 text-sm text-text-secondary hover:bg-text-primary/10"
        onclick={() => cycleTheme()}
        title={`Theme: ${activeTheme.label} — click to cycle`}
        aria-label="Cycle theme"
      >
        <span aria-hidden="true">{activeTheme.ico}</span>
        <span class="ml-1">{activeTheme.label}</span>
      </button>
      <button
        type="button"
        class="rounded-lg bg-accent px-4 py-2 font-semibold text-white shadow transition hover:bg-accent-bright focus:outline-none focus:ring-2 focus:ring-accent-bright"
        onclick={onImport}>Load city</button
      >
    </div>
  </header>

  <section class="mx-auto mt-8 max-w-6xl">
    {#if $cityList.length === 0}
      <EmptyState {onImport} {onFileDrop} />
    {:else}
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {#each $cityList as entry (entry.plan.id)}
          <CityCard
            {entry}
            onOpen={() => navigate({ name: 'city', planId: entry.plan.id })}
            onRename={() => handleRename(entry.plan.id, entry.plan.name)}
            onDelete={() =>
              handleDelete(entry.plan.importedCityId, entry.plan.name)}
            onExport={() => {
              if (entry.importedCity) void downloadImportJson(entry.importedCity)
            }}
          />
        {/each}
      </div>
    {/if}
  </section>
</main>
